import {
  CategorySubtype,
  CategoryToLabelMappingProduct,
  CategoryType,
} from "@digits-graphql/frontend/graphql-bearer"
import colors from "@digits-shared/themes/colors"
import { css } from "styled-components"
import { type ActiveCategoryType } from "src/frontend/components/OS/Applications/CategoryManager/molecules/categoryManagerMolecule"

const dataCategoryType = (categoryType: ActiveCategoryType) =>
  `[data-category-type="${categoryType}"]`

export const CATEGORY_COLOR_MAP = new Map<CategoryType, string>([
  [CategoryType.Assets, "#2C9D80"],
  [CategoryType.Liabilities, "#F9A455"],
  [CategoryType.Equity, "#7B82D0"],
  [CategoryType.Income, "#33BDD0"],
  [CategoryType.CostOfGoodsSold, "#9D498F"],
  [CategoryType.Expenses, "#FA8072"],
  [CategoryType.OtherIncome, "#1E91A1"],
  [CategoryType.OtherExpenses, "#DA576E"],
  [CategoryType.UnknownType, colors.gray],
])

export const CATEGORY_NUMBER_PREFIX_MAP = new Map<CategoryType, string>([
  [CategoryType.Assets, "1"],
  [CategoryType.Liabilities, "2"],
  [CategoryType.Equity, "3"],
  [CategoryType.Income, "4"],
  [CategoryType.CostOfGoodsSold, "5"],
  [CategoryType.Expenses, "6"],
  [CategoryType.OtherIncome, "8"],
  [CategoryType.OtherExpenses, "9"],
  [CategoryType.UnknownType, "0"],
])

export const CATEGORY_TYPE_ICON_MAP = new Map<CategoryType, string>([
  [CategoryType.Assets, "CurrentAssets"],
  [CategoryType.Liabilities, "CurrentLiabilities"],
  [CategoryType.Equity, "PreferredStock"],
  [CategoryType.Income, "Revenue"],
  [CategoryType.CostOfGoodsSold, "ProductCosts"],
  [CategoryType.Expenses, "General"],
  [CategoryType.OtherIncome, "OtherIncome"],
  [CategoryType.OtherExpenses, "Ground"],
  [CategoryType.UnknownType, "General"],
])

export const CATEGORY_TYPE_LABEL_MAP = new Map<CategoryType, string>([
  [CategoryType.Assets, "Asset"],
  [CategoryType.Liabilities, "Liability"],
  [CategoryType.Equity, "Equity"],
  [CategoryType.Income, "Revenue"],
  [CategoryType.CostOfGoodsSold, "Cost of Revenue"],
  [CategoryType.Expenses, "Operating Expenses"],
  [CategoryType.OtherIncome, "Other Income"],
  [CategoryType.OtherExpenses, "Other Expenses"],
  [CategoryType.UnknownType, "Unknown"],
])

export const UNCATEGORIZED_SUBTYPES = [
  CategorySubtype.UncategorizedAssets,
  CategorySubtype.UncategorizedEquity,
  CategorySubtype.UncategorizedExpense,
  CategorySubtype.UncategorizedIncome,
]

export const CATEGORY_SUBTYPES_MAP = new Map<CategoryType, CategorySubtype[]>([
  [
    CategoryType.Assets,
    [
      CategorySubtype.AccountsReceivable,
      CategorySubtype.AccruedRevenue,
      CategorySubtype.AssetClearing,
      CategorySubtype.BankAccounts,
      CategorySubtype.FixedAssets,
      CategorySubtype.Goodwill,
      CategorySubtype.IntangibleAssets,
      CategorySubtype.Inventory,
      CategorySubtype.Investments,
      CategorySubtype.OtherCurrentAssets,
      CategorySubtype.OtherLongTermAssets,
      CategorySubtype.PrepaidExpenses,
      CategorySubtype.ShareholderLoans,
      CategorySubtype.UncategorizedAssets,
    ],
  ],
  [
    CategoryType.Liabilities,
    [
      CategorySubtype.AccountsPayable,
      CategorySubtype.AccruedExpenses,
      CategorySubtype.ConvertibleNotes,
      CategorySubtype.CreditCards,
      CategorySubtype.DeferredRevenue,
      CategorySubtype.LiabilityClearing,
      CategorySubtype.LongTermDebt,
      CategorySubtype.OtherCurrentLiabilities,
      CategorySubtype.OtherLongTermLiabilities,
      CategorySubtype.ShortTermDebt,
    ],
  ],
  [
    CategoryType.Equity,
    [
      CategorySubtype.AdditionalPaidInCapital,
      CategorySubtype.CommonStock,
      CategorySubtype.OtherComprehensiveIncome,
      CategorySubtype.OwnersEquity,
      CategorySubtype.PreferredStock,
      CategorySubtype.RetainedEarnings,
      CategorySubtype.Safes,
      CategorySubtype.TreasuryStock,
      CategorySubtype.UncategorizedEquity,
    ],
  ],
  [
    CategoryType.Income,
    [
      CategorySubtype.PartnerRevenue,
      CategorySubtype.RefundsAndDiscounts,
      CategorySubtype.SalesRevenue,
      CategorySubtype.SubscriptionRevenue,
      CategorySubtype.TransactionRevenue,
      CategorySubtype.UncategorizedIncome,
    ],
  ],
  [
    CategoryType.CostOfGoodsSold,
    [
      CategorySubtype.CostOfGoods,
      CategorySubtype.CostOfLabor,
      CategorySubtype.HostingFees,
      CategorySubtype.OtherCostOfRevenue,
      CategorySubtype.PaymentProcessingFees,
    ],
  ],
  [
    CategoryType.Expenses,
    [
      CategorySubtype.BusinessApplicationsAndSoftware,
      CategorySubtype.Facilities,
      CategorySubtype.GeneralOperations,
      CategorySubtype.PayrollExpenses,
      CategorySubtype.ProfessionalFees,
      CategorySubtype.SalesAndMarketing,
      CategorySubtype.SuppliesAndMaterials,
      CategorySubtype.TravelAndEntertainment,
      CategorySubtype.UncategorizedExpense,
    ],
  ],
  [
    CategoryType.OtherIncome,
    [
      CategorySubtype.CreditCardRewards,
      CategorySubtype.InterestAndDividends,
      CategorySubtype.OtherMiscellaneousIncome,
    ],
  ],
  [
    CategoryType.OtherExpenses,
    [
      CategorySubtype.BadDebtExpense,
      CategorySubtype.DepreciationAndAmortization,
      CategorySubtype.InterestExpense,
      CategorySubtype.OtherMiscellaneousExpenses,
      CategorySubtype.Taxes,
    ],
  ],
  [CategoryType.UnknownType, []],
])

export const CATEGORY_SUBTYPE_DISPLAY_MAP = new Map<
  CategorySubtype,
  { label: string; type: CategoryType; displayKey: string }
>([
  [
    CategorySubtype.AccountsPayable,
    { label: "Accounts Payable", type: CategoryType.Liabilities, displayKey: "CoinsHand02" },
  ],
  [
    CategorySubtype.AccountsReceivable,
    { label: "Accounts Receivable", type: CategoryType.Assets, displayKey: "CoinsHand" },
  ],
  [
    CategorySubtype.AccruedExpenses,
    { label: "Accrued Expenses", type: CategoryType.Liabilities, displayKey: "CalendarDollar01" },
  ],
  [
    CategorySubtype.AccruedRevenue,
    { label: "Accrued Revenue", type: CategoryType.Assets, displayKey: "CoinsHand" },
  ],
  [
    CategorySubtype.AdditionalPaidInCapital,
    { label: "Additional Paid-In Capital", type: CategoryType.Equity, displayKey: "CoinsPlus01" },
  ],
  [
    CategorySubtype.AssetClearing,
    { label: "Asset Clearing", type: CategoryType.Assets, displayKey: "SwitchHorizontal01" },
  ],
  [
    CategorySubtype.BadDebtExpense,
    { label: "Bad Debt Expense", type: CategoryType.OtherExpenses, displayKey: "MoneyBagAlert" },
  ],
  [
    CategorySubtype.BankAccounts,
    { label: "Bank Accounts", type: CategoryType.Assets, displayKey: "Bank" },
  ],
  [
    CategorySubtype.BusinessApplicationsAndSoftware,
    {
      label: "Business Applications & Software",
      type: CategoryType.Expenses,
      displayKey: "LaptopCloud01",
    },
  ],
  [
    CategorySubtype.CommonStock,
    { label: "Common Stock", type: CategoryType.Equity, displayKey: "Certificate01" },
  ],
  [
    CategorySubtype.ConvertibleNotes,
    { label: "Convertible Notes", type: CategoryType.Liabilities, displayKey: "Certificate01" },
  ],
  [
    CategorySubtype.CostOfGoods,
    { label: "Cost of Goods", type: CategoryType.CostOfGoodsSold, displayKey: "BagDollar01" },
  ],
  [
    CategorySubtype.CostOfLabor,
    { label: "Cost of Labor", type: CategoryType.CostOfGoodsSold, displayKey: "UserDollar" },
  ],
  [
    CategorySubtype.CreditCardRewards,
    { label: "Credit Card Rewards", type: CategoryType.OtherIncome, displayKey: "CreditCardCheck" },
  ],
  [
    CategorySubtype.CreditCards,
    { label: "Credit Cards", type: CategoryType.Liabilities, displayKey: "CreditCard02" },
  ],
  [
    CategorySubtype.DeferredRevenue,
    { label: "Deferred Revenue", type: CategoryType.Liabilities, displayKey: "ClockDollar" },
  ],
  [
    CategorySubtype.DepreciationAndAmortization,
    {
      label: "Depreciation & Amortization",
      type: CategoryType.OtherExpenses,
      displayKey: "TrendDown01",
    },
  ],
  [
    CategorySubtype.Facilities,
    { label: "Facilities", type: CategoryType.Expenses, displayKey: "Building07" },
  ],
  [
    CategorySubtype.FixedAssets,
    { label: "Fixed Assets", type: CategoryType.Assets, displayKey: "Couch" },
  ],
  [
    CategorySubtype.GeneralOperations,
    { label: "General Operations", type: CategoryType.Expenses, displayKey: "Dataflow04" },
  ],
  [
    CategorySubtype.Goodwill,
    { label: "Goodwill", type: CategoryType.Assets, displayKey: "CoinsHeart01" },
  ],
  [
    CategorySubtype.HostingFees,
    { label: "Hosting Fees", type: CategoryType.CostOfGoodsSold, displayKey: "UploadCloud01" },
  ],
  [
    CategorySubtype.IntangibleAssets,
    { label: "Intangible Assets", type: CategoryType.Assets, displayKey: "Lightbulb05" },
  ],
  [
    CategorySubtype.InterestAndDividends,
    { label: "Interest & Dividends", type: CategoryType.Income, displayKey: "Percent02" },
  ],
  [
    CategorySubtype.InterestExpense,
    { label: "Interest Expense", type: CategoryType.OtherExpenses, displayKey: "Percent02" },
  ],
  [
    CategorySubtype.Inventory,
    { label: "Inventory", type: CategoryType.Assets, displayKey: "PackageCheck" },
  ],
  [
    CategorySubtype.Investments,
    { label: "Investments", type: CategoryType.Assets, displayKey: "TrendUp01" },
  ],
  [
    CategorySubtype.LiabilityClearing,
    {
      label: "Liability Clearing",
      type: CategoryType.Liabilities,
      displayKey: "SwitchHorizontal01",
    },
  ],
  [
    CategorySubtype.LongTermDebt,
    { label: "Long-Term Debt", type: CategoryType.Liabilities, displayKey: "MoneyBag" },
  ],
  [
    CategorySubtype.OtherComprehensiveIncome,
    {
      label: "Other Comprehensive Income",
      type: CategoryType.Equity,
      displayKey: "SwitchVertical01",
    },
  ],
  [
    CategorySubtype.OtherCostOfRevenue,
    {
      label: "Other Cost of Revenue",
      type: CategoryType.CostOfGoodsSold,
      displayKey: "BagDollar01",
    },
  ],
  [
    CategorySubtype.OtherCurrentAssets,
    { label: "Other Current Assets", type: CategoryType.Assets, displayKey: "CoinsStacked03" },
  ],
  [
    CategorySubtype.OtherCurrentLiabilities,
    { label: "Other Current Liabilities", type: CategoryType.Liabilities, displayKey: "Iou" },
  ],
  [
    CategorySubtype.OtherLongTermAssets,
    { label: "Other Long-Term Assets", type: CategoryType.Assets, displayKey: "Wallet02" },
  ],
  [
    CategorySubtype.OtherLongTermLiabilities,
    { label: "Other Long-Term Liabilities", type: CategoryType.Liabilities, displayKey: "Iou" },
  ],
  [
    CategorySubtype.OtherMiscellaneousExpenses,
    {
      label: "Other Miscellaneous Expenses",
      type: CategoryType.OtherExpenses,
      displayKey: "DollarTransaction",
    },
  ],
  [
    CategorySubtype.OtherMiscellaneousIncome,
    {
      label: "Other Miscellaneous Income",
      type: CategoryType.OtherIncome,
      displayKey: "PieChart05",
    },
  ],
  [
    CategorySubtype.OwnersEquity,
    { label: "Owner's Equity", type: CategoryType.Equity, displayKey: "UserCoins01" },
  ],
  [
    CategorySubtype.PartnerRevenue,
    { label: "Partner Revenue", type: CategoryType.Income, displayKey: "HandShakeDollar01" },
  ],
  [
    CategorySubtype.PaymentProcessingFees,
    {
      label: "Payment Processing Fees",
      type: CategoryType.CostOfGoodsSold,
      displayKey: "CreditCardScissors",
    },
  ],
  [
    CategorySubtype.PayrollExpenses,
    { label: "Payroll Expenses", type: CategoryType.Expenses, displayKey: "UserDollar" },
  ],
  [
    CategorySubtype.PreferredStock,
    { label: "Preferred Stock", type: CategoryType.Equity, displayKey: "Certificate01" },
  ],
  [
    CategorySubtype.PrepaidExpenses,
    { label: "Prepaid Expenses", type: CategoryType.Assets, displayKey: "CalendarDollar01" },
  ],
  [
    CategorySubtype.ProfessionalFees,
    { label: "Professional Fees", type: CategoryType.Expenses, displayKey: "Award03" },
  ],
  [
    CategorySubtype.RefundsAndDiscounts,
    { label: "Refunds & Discounts", type: CategoryType.Income, displayKey: "Sale03" },
  ],
  [
    CategorySubtype.RetainedEarnings,
    { label: "Retained Earnings", type: CategoryType.Equity, displayKey: "EnvelopeDollar01" },
  ],
  [
    CategorySubtype.Safes,
    { label: "SAFEs", type: CategoryType.Equity, displayKey: "Certificate01" },
  ],
  [
    CategorySubtype.SalesAndMarketing,
    { label: "Sales & Marketing", type: CategoryType.Expenses, displayKey: "Announcement04" },
  ],
  [
    CategorySubtype.SalesRevenue,
    { label: "Sales Revenue", type: CategoryType.Income, displayKey: "TagDollar" },
  ],
  [
    CategorySubtype.ShareholderLoans,
    { label: "Shareholder Loans", type: CategoryType.Assets, displayKey: "DollarHand" },
  ],
  [
    CategorySubtype.ShortTermDebt,
    { label: "Short-Term Debt", type: CategoryType.Liabilities, displayKey: "MoneyBag" },
  ],
  [
    CategorySubtype.SubscriptionRevenue,
    { label: "Subscription Revenue", type: CategoryType.Income, displayKey: "RefreshCw04" },
  ],
  [
    CategorySubtype.SuppliesAndMaterials,
    { label: "Supplies & Materials", type: CategoryType.Expenses, displayKey: "Paperclip" },
  ],
  [
    CategorySubtype.Taxes,
    { label: "Taxes", type: CategoryType.Expenses, displayKey: "Scissors02" },
  ],
  [
    CategorySubtype.TransactionRevenue,
    { label: "Transaction Revenue", type: CategoryType.Income, displayKey: "Transaction" },
  ],
  [
    CategorySubtype.TravelAndEntertainment,
    { label: "Travel & Entertainment", type: CategoryType.Expenses, displayKey: "Plane" },
  ],
  [
    CategorySubtype.TreasuryStock,
    { label: "Treasury Stock", type: CategoryType.Equity, displayKey: "Certificate01" },
  ],
  [
    CategorySubtype.UncategorizedAssets,
    { label: "Uncategorized Assets", type: CategoryType.Assets, displayKey: "QuestionTransaction" },
  ],
  [
    CategorySubtype.UncategorizedEquity,
    { label: "Uncategorized Equity", type: CategoryType.Equity, displayKey: "QuestionTransaction" },
  ],
  [
    CategorySubtype.UncategorizedExpense,
    {
      label: "Uncategorized Expenses",
      type: CategoryType.Expenses,
      displayKey: "QuestionTransaction",
    },
  ],
  [
    CategorySubtype.UncategorizedIncome,
    { label: "Uncategorized Income", type: CategoryType.Income, displayKey: "QuestionTransaction" },
  ],
  [CategorySubtype.Other, { label: "Other", type: CategoryType.UnknownType, displayKey: "Tag01" }],
])

export const CATEGORY_PROPERTIES = [
  {
    label: "Contra",
    icon: "Brackets",
    tooltip:
      "Categories that reduce the balance of their parent category, typically with transactions that have the opposite signage",
    types: [
      CategoryType.Assets,
      CategoryType.Liabilities,
      CategoryType.Equity,
      CategoryType.Income,
      CategoryType.CostOfGoodsSold,
      CategoryType.Expenses,
      CategoryType.OtherExpenses,
      CategoryType.OtherIncome,
    ],
  },
  {
    label: "Reconcile",
    icon: "Scales02",
    tooltip: "Categories that require to be reconciled on a recurring basis",
    types: [CategoryType.Assets, CategoryType.Liabilities, CategoryType.Equity],
  },
  {
    label: "1099 Qualified",
    icon: "CheckVerified02",
    tooltip: "Categories that need to be tracked for 1099 filing purposes",
    types: [
      CategoryType.Assets,
      CategoryType.Income,
      CategoryType.Expenses,
      CategoryType.CostOfGoodsSold,
      CategoryType.OtherExpenses,
      CategoryType.OtherIncome,
    ],
  },
  {
    label: "Recurring",
    icon: "RefreshCw05",
    tooltip: "Categories with recurring transactions",
    types: [
      CategoryType.Income,
      CategoryType.CostOfGoodsSold,
      CategoryType.Expenses,
      CategoryType.OtherExpenses,
      CategoryType.OtherIncome,
    ],
  },
  {
    label: "Clearing",
    icon: "SwitchHorizontal01",
    tooltip: "Categories that are expected to net out to zero on a recurring basis",
    types: [CategoryType.Assets, CategoryType.Liabilities, CategoryType.Equity],
  },
]

export const CATEGORY_TO_LABEL_MAPPING_PRODUCT_DISPLAY_NAME = {
  [CategoryToLabelMappingProduct.DigitsPayAP]: "Digits Pay",
  [CategoryToLabelMappingProduct.DigitsPayAR]: "Digits Invoicing",
  [CategoryToLabelMappingProduct.UnknownProduct]: "Unknown",
}

export const CSS_COLOR_VARS = css`
  --category-manager-panel-background: ${colors.translucentWhite50};
  --category-manager-header-background: ${colors.teal};
  --category-manager-tile-background: ${colors.white};

  ${Array.from(CATEGORY_COLOR_MAP.entries()).map(
    ([categoryType, color]) => css`
      &${dataCategoryType(categoryType)} {
        --category-manager-header-background: ${color};
      }
    `
  )};

  &${dataCategoryType("inactive")} {
    --category-manager-header-background: ${colors.secondary50};
  }
`
